import { PivotFilterInput } from './graphql';
import { Dimension, PivotConfig } from './PivotConfigurator';

// Field bundle registry - maps simple bundle names to actual dimension IDs
// This bridges the gap between flat specs and real GraphQL dimensions

export interface FieldBundle {
  idDim?: string;
  labelDim?: string;
}

export const FIELD_BUNDLE_REGISTRY: Record<string, FieldBundle> = {
  collector: {
    idDim: 'collectorId',
    labelDim: 'collectorName',
  },
  vehicle: {
    idDim: 'vehicleId',
    labelDim: 'vehiclePlate', // Use plate number as display label
  },
  collectionPoint: {
    idDim: 'collectionPointId',
    labelDim: 'collectionPointName',
  },
  material: {
    idDim: 'materialId',
    labelDim: 'materialName',
  },
  routeLine: {
    idDim: 'routeLineId',
    labelDim: 'collectionOrderCode', // Display collection order code while grouping by ID
  },
  region: {
    idDim: 'regionName',
    labelDim: 'regionName', // display & filter match by name for this case
  },
  active: {
    idDim: 'isActive',
    labelDim: 'isActiveLabel', // filter target vs display
  },
  month: {
    labelDim: 'month', // computed, do not filter
  },
  dateCollected: {
    idDim: 'dateCollected',
  },
  isActive: {
    idDim: 'isActive',
  },
};

// Flat spec interface - simple configuration object
export interface FlatSpec {
  [bundleName: string]: {
    select?: boolean; // Show as dimension
    selectWhere?: 'row' | 'col'; // Placement (default: row)
    filterId?: string | number; // ID-based filter
    filter?: string; // Value-based filter ('true'/'false' for booleans)
    from?: string; // ISO date for range start
    to?: string; // ISO date for range end
  };
  // Optional global date range that applies to dateCollected
  dateRange?: {
    from?: string;
    to?: string;
  };
}

// Predefined flat specs for Collections
export const COLLECTIONS_FLAT_SPECS: Record<string, { label: string; spec: FlatSpec }> = {
  'basic-monthly': {
    label: 'Μηνιαία ανά Περιοχή',
    spec: {
      month: { select: true },
      region: { select: true, selectWhere: 'col' },
      active: { filter: 'true' },
    },
  },
  'collector-analysis': {
    label: 'Απόδοση Συλλεκτών',
    spec: {
      collector: { select: true },
      month: { select: true, selectWhere: 'col' },
      active: { filter: 'true' },
    },
  },
  'route-breakdown': {
    label: 'Ανάλυση Διαδρομών',
    spec: {
      routeLine: { select: true },
      region: { select: true, selectWhere: 'col' },
      active: { filter: 'true' },
    },
  },
  'date-range': {
    label: 'Ανάλυση Εύρους Ημερομηνιών',
    spec: {
      dateCollected: { select: true },
      region: { select: true, selectWhere: 'col' },
      dateRange: {
        from: '2024-01-01',
        to: '2024-12-31',
      },
    },
  },
  'recent-collections': {
    label: 'Πρόσφατες Συλλογές (Τελευταίες 30 Ημέρες)',
    spec: {
      collector: { select: true },
      region: { select: true, selectWhere: 'col' },
      active: { filter: 'true' },
      dateRange: {
        from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      },
    },
  },
  'material-breakdown': {
    label: 'Ανάλυση Υλικών ανά Περιοχή',
    spec: {
      material: { select: true },
      region: { select: true, selectWhere: 'col' },
      active: { filter: 'true' },
    },
  },
  'material-collector': {
    label: 'Ανάλυση Υλικών ανά Συλλέκτη',
    spec: {
      material: { select: true },
      collector: { select: true, selectWhere: 'col' },
      active: { filter: 'true' },
    },
  },
};

// Transform flat spec into pivot configuration and filters
export function transformFlatSpec(
  flatSpec: FlatSpec,
  availableDimensions: Dimension[],
): {
  config: PivotConfig;
  filters?: PivotFilterInput[];
  dateRange?: { from?: string; to?: string };
} {
  const rows: Dimension[] = [];
  const cols: Dimension[] = [];
  const filters: PivotFilterInput[] = [];
  const usedDimensionIds = new Set<string>();

  // Process global dateRange first
  const dateRange = flatSpec.dateRange;
  if (dateRange && (dateRange.from || dateRange.to)) {
    if (dateRange.from && dateRange.to) {
      filters.push({
        dimId: 'dateCollected',
        operator: 'between',
        values: [`${dateRange.from}T00:00:00`, `${dateRange.to}T23:59:59`],
      });
    } else if (dateRange.from) {
      filters.push({
        dimId: 'dateCollected',
        operator: 'gte',
        values: [`${dateRange.from}T00:00:00`],
      });
    } else if (dateRange.to) {
      filters.push({
        dimId: 'dateCollected',
        operator: 'lte',
        values: [`${dateRange.to}T23:59:59`],
      });
    }
  }

  // Process each bundle in the flat spec (skip dateRange key)
  Object.entries(flatSpec).forEach(([bundleName, settings]) => {
    if (bundleName === 'dateRange') return;

    const bundle = FIELD_BUNDLE_REGISTRY[bundleName];
    if (!bundle) {
      console.warn(`Unknown bundle: ${bundleName}`);
      return;
    }

    // Handle dimension selection
    if (settings.select) {
      // Prefer labelDim over idDim for display
      const dimId = bundle.labelDim || bundle.idDim;
      if (dimId) {
        const dimension = availableDimensions.find((d) => d.id === dimId);
        if (dimension) {
          const targetZone = settings.selectWhere === 'col' ? cols : rows;
          targetZone.push(dimension);
          usedDimensionIds.add(dimId);
        }
      }
    }

    // Handle filters
    if (settings.filterId && bundle.idDim) {
      filters.push({
        dimId: bundle.idDim,
        operator: 'eq',
        values: [String(settings.filterId)],
      });
    }

    if (settings.filter && bundle.idDim) {
      filters.push({
        dimId: bundle.idDim,
        operator: 'eq',
        values: [settings.filter],
      });
    }

    // Only apply date range filters to dateCollected bundle
    if (bundleName === 'dateCollected' && bundle.idDim) {
      if (settings.from && settings.to) {
        filters.push({
          dimId: bundle.idDim,
          operator: 'between',
          values: [settings.from, settings.to],
        });
      } else if (settings.from) {
        filters.push({
          dimId: bundle.idDim,
          operator: 'gte',
          values: [settings.from],
        });
      } else if (settings.to) {
        filters.push({
          dimId: bundle.idDim,
          operator: 'lte',
          values: [settings.to],
        });
      }
    }
  });

  // Available dimensions are those not used in rows or cols
  const available = availableDimensions.filter((d) => !usedDimensionIds.has(d.id));

  return {
    config: { available, rows, cols },
    filters: filters.length === 0 ? undefined : filters,
    dateRange,
  };
}
